import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiAlertTriangle, FiClock, FiSettings, FiUser, FiUsers, FiX } from 'react-icons/fi';
import type { AgentLicense, HRBootstrap, HREmployee } from './hrData';
import {
  acknowledgeHREvent, allocatePto, cancelTimeOffRequest, createHREvent, createLicense, createPaidHoliday, createPtoPlan,
  createPtoType, createTimeOffRequest, decideTimeOffRequest, deleteHREvent, deleteLicense, deletePaidHoliday, deletePtoPlan,
  deletePtoType, getHRBootstrap, saveHRSettings, setLicenseCommand, updateHREmployee, updateLicense,
} from '../../services/hrService';
import HRDashboard from './HRDashboard';
import HRDirectory from './HRDirectory';
import HRTimeOff from './HRTimeOff';
import HRClock from './HRClock';
import HRSettings from './HRSettings';
import HREvents from './HREvents';
import { usDate } from './licenseUtils';
import CommandIssuePanel from '../momentumcommand/CommandIssuePanel';
import type { CommandIssue } from '../../context/CommandIssueContext';
import '../../styles/HRCenter.css';

type Tab = 'dashboard' | 'directory' | 'timeoff' | 'clock' | 'settings';

interface CommandDraft {
  issue: CommandIssue;
  licenseId: number;
}

const HRCenterPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [data, setData] = useState<HRBootstrap | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [commandDraft, setCommandDraft] = useState<CommandDraft | null>(null);
  const [commandPanelKey, setCommandPanelKey] = useState(0);
  // The panel reports every save; link the license to the task only once.
  const linkedLicenseRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await getHRBootstrap());
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load HR Center.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Runs one change against the API, then reloads. Resolves false (and shows the error) if it failed. */
  const run = useCallback(async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  const openLicenseCommand = useCallback((employee: HREmployee, license: AgentLicense) => {
    linkedLicenseRef.current = null;
    setCommandPanelKey(k => k + 1);
    setCommandDraft({
      licenseId: license.id,
      issue: {
        id: '__new__',
        code: 'HR-NEW',
        title: `License renewal — ${employee.name} (${license.state})`,
        description: `${employee.name}'s ${license.state} producer license${license.licenseNumber ? ` #${license.licenseNumber}` : ''} (${license.linesOfAuthority}) expires ${usDate(license.expirationDate)}. Reach out to confirm renewal status.`,
        status: 'todo',
        categoryId: 5,
        assigneeType: 0,
        comments: [],
      },
    });
  }, []);

  const closeLicenseCommand = useCallback(() => setCommandDraft(null), []);

  const handleCommandSaved = useCallback((updated: CommandIssue) => {
    if (!commandDraft) return;
    setCommandDraft({ ...commandDraft, issue: updated });
    if (updated.id && updated.id !== '__new__' && linkedLicenseRef.current !== commandDraft.licenseId) {
      linkedLicenseRef.current = commandDraft.licenseId;
      run(() => setLicenseCommand(commandDraft.licenseId, updated.id));
    }
  }, [commandDraft, run]);

  if (!data) {
    return (
      <div className="hr">
        <div className="hr__eyebrow"><span>HR Center</span></div>
        {loadError ? (
          <div className="hr__alert" role="alert">
            <span>{loadError}</span>
            <button type="button" className="hr__alert-action" onClick={load}>Retry</button>
          </div>
        ) : (
          <p className="hr__blurb">Loading HR Center…</p>
        )}
      </div>
    );
  }

  const { me, isHrAdmin: admin } = data;
  const pendingCount = data.pendingRequests.length;

  const TABS: Array<{ key: Tab; label: string; icon: React.ReactNode; adminOnly?: boolean }> = [
    { key: 'dashboard', label: 'Dashboard', icon: <FiUsers size={14} /> },
    { key: 'directory', label: 'Directory', icon: <FiUsers size={14} />, adminOnly: true },
    { key: 'timeoff', label: 'Time Off', icon: <FiUser size={14} /> },
    { key: 'clock', label: 'Clock', icon: <FiClock size={14} /> },
    { key: 'settings', label: 'Settings', icon: <FiSettings size={14} />, adminOnly: true },
  ];

  return (
    <div className="hr">
      <header className="hr__head">
        <div>
          <div className="hr__eyebrow">
            <span>HR Center</span>
          </div>
          {/* The name NowCerts gave at login (what the sidebar shows); UserInformation may only have the email. */}
          <h1 className="hr__title">Good to see you, {(localStorage.getItem('firstName') || '').trim() || me.name.split(' ')[0]}</h1>
          <p className="hr__blurb">
            {admin
              ? 'You have Admin Control — the directory, PTO configuration, and every request are visible.'
              : 'Your time off, your clock, and what’s happening across the agency.'}
          </p>
        </div>
      </header>

      <nav className="hr__tabs">
        {TABS.filter(t => !t.adminOnly || admin).map(t => (
          <button
            key={t.key}
            type="button"
            className={`hr__tab${tab === t.key ? ' hr__tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon}
            {t.label}
            {t.key === 'timeoff' && admin && pendingCount > 0 && <span className="hr__tab-badge">{pendingCount}</span>}
          </button>
        ))}
      </nav>

      {actionError && (
        <div className="hr__alert" role="alert">
          <span>{actionError}</span>
          <button type="button" className="hr__alert-action" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      <div className="hr__body">
        {tab === 'dashboard' && (
          <>
            <HRDashboard
              me={me} employees={data.employees} kpis={data.kpis} happenings={data.happenings} admin={admin}
              licenses={admin ? { alertDays: data.licenseAlertDays, onCreateCommand: openLicenseCommand } : undefined}
            />
            <HREvents
              admin={admin}
              employees={data.employees}
              events={data.events}
              busy={busy}
              onCreate={payload => run(() => createHREvent(payload))}
              onDelete={id => run(() => deleteHREvent(id))}
              onAcknowledge={id => run(() => acknowledgeHREvent(id))}
            />
          </>
        )}
        {tab === 'directory' && admin && (
          <HRDirectory
            employees={data.employees}
            ptoPlans={data.ptoPlans}
            busy={busy}
            onAllocatePto={(id, hours) => run(() => allocatePto(id, hours))}
            onUpdateEmployee={(id, payload) => run(() => updateHREmployee(id, payload))}
            licenses={{
              alertDays: data.licenseAlertDays,
              onAdd: (employeeId, license) => run(() => createLicense(employeeId, license)),
              onUpdate: (licenseId, license) => run(() => updateLicense(licenseId, license)),
              onRemove: licenseId => run(() => deleteLicense(licenseId)),
              onCreateCommand: openLicenseCommand,
            }}
          />
        )}
        {tab === 'timeoff' && (
          <HRTimeOff
            me={me}
            admin={admin}
            employees={data.employees}
            myRequests={data.myRequests}
            pendingRequests={data.pendingRequests}
            ptoTypes={data.ptoTypes}
            busy={busy}
            onSubmit={payload => run(() => createTimeOffRequest(payload))}
            onRespond={(id, status) => run(() => decideTimeOffRequest(id, status))}
            onCancel={id => run(() => cancelTimeOffRequest(id))}
          />
        )}
        {tab === 'clock' && <HRClock me={me} admin={admin} employees={data.employees} />}
        {tab === 'settings' && admin && (
          <HRSettings
            ptoPlans={data.ptoPlans} ptoTypes={data.ptoTypes} holidays={data.holidays} busy={busy}
            onAddPlan={name => run(() => createPtoPlan(name))}
            onRemovePlan={id => run(() => deletePtoPlan(id))}
            onAddType={(name, color, deductsBalance) => run(() => createPtoType(name, color, deductsBalance))}
            onRemoveType={id => run(() => deletePtoType(id))}
            onAddHoliday={(date, name) => run(() => createPaidHoliday(date, name))}
            onRemoveHoliday={id => run(() => deletePaidHoliday(id))}
            licenseAlerts={{ days: data.licenseAlertDays, onChange: days => run(() => saveHRSettings(days)) }}
          />
        )}
      </div>

      <AnimatePresence>
        {commandDraft && (
          <motion.div
            className="hr-cmd-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLicenseCommand}
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.18 }}
              onClick={ev => ev.stopPropagation()}
              className="hr-cmd-modal"
            >
              <div className="hr-cmd-modal__header">
                <FiAlertTriangle size={13} className="hr-cmd-modal__icon" aria-hidden="true" />
                <span className="hr-cmd-modal__title">Create License Renewal Command</span>
                <button type="button" className="hr-cmd-modal__close" onClick={closeLicenseCommand} aria-label="Close">
                  <FiX size={16} />
                </button>
              </div>
              <div className="hr-cmd-modal__body">
                <CommandIssuePanel
                  key={commandPanelKey}
                  issue={commandDraft.issue}
                  mode="create"
                  splitTitleRow
                  onBack={closeLicenseCommand}
                  onSave={handleCommandSaved}
                  onSaveSilent={handleCommandSaved}
                  onClose={closeLicenseCommand}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HRCenterPage;
